import './App.css';
import {useEffect, useRef, useState} from "react";

import {multi_viseme2imageClipSequence} from "./VisemeUtilsMulti";
import {extractFramesFromH264} from "./VideoUtils";

import {pcmHexToWavBlob} from "./AudioUtils";

// check window.parent
const IS_IN_IFRAME = Boolean(window.parent !== window && window.parent !== window.self);

const ACCESS_TOKEN = window.location.search.match(/token=([^&]+)/)?.[1] || null;

let previousEmotion = "idle";

console.log(`ACCESS_TOKEN: ${ACCESS_TOKEN}`);

async function fetchWithAuth(url, options = {}) {
    if (!ACCESS_TOKEN) {
        throw new Error("No access token provided");
    }

    const headers = {
        ...options.headers,
        "Authorization": `Bearer ${ACCESS_TOKEN}`,
    };

    return fetch(url, {
        ...options,
        headers,
    });
}

// 전역 AudioContext (Web Audio로 재생할 때 필수)
export const ctx = new (window.AudioContext || window.webkitAudioContext)();

// 한 번만 호출되는 해제 함수
function unlockAudio() {
    if (ctx.state === 'suspended') ctx.resume();      // Web Audio용
    // HTMLMediaElement도 미리 살짝 재생 시도해 두면 더 안전
    const probe = new Audio();
    probe.muted = true;
    probe.play().catch(() => {/* 무시 */
    });

    window.removeEventListener('pointerup', unlockAudio);
}

// 페이지 어디든 첫 pointerup(클릭·터치)에서 잠금 해제
window.addEventListener('pointerup', unlockAudio, {once: true});

export async function speak(blob) {
    if (IS_IN_IFRAME && window.parentCallback) {
        if (window.parentCallback) {
            console.log("Calling parent callback to play audio");
            window.parentCallback({
                "type": "aime-speak-response",
                "blob": blob
            })
        }
    } else {
        // 1) unlockAudio() 가 이미 실행돼 ctx.state === 'running'
        const buf = await blob.arrayBuffer();
        const audioBuf = await ctx.decodeAudioData(buf);

        const src = ctx.createBufferSource();
        src.buffer = audioBuf;
        src.connect(ctx.destination);
        src.start();
    }
}

let gl = null;

const programCache = {};
const textureCache = {};
const bufferCache = {};

let configData = null;
let npcData = null;

const previewZip = {};

let previewLoaded = false;

let lipIdleFrame = 0;
let eyeIdleFrame = 0;

const lipIdleAnim = [];
const lipAnimation = [];

let lipAnimationEnabled = false;

const eyeIdleAnim = [];
const eyeAnimation = [];

let pcmTimeout = null;

// initialize canvas for webgl
function initCanvas() {
    const canvas = document.getElementById("canvas");
    if (!canvas) return;

    gl = canvas.getContext("webgl");
    if (!gl) {
        console.error("WebGL not supported, falling back to experimental-webgl");
        return;
    }

    canvas.width = 1080;
    canvas.height = 1920;

    // Set the viewport to match the canvas size
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.clearColor(0.0, 0.0, 0.0, 0.0); // Set clear color to black
    gl.clear(gl.COLOR_BUFFER_BIT); // Clear the color buffer
}

function loadShader(type, source) {
    console.log("Loading shader of type:", type);

    const shader = gl.createShader(type);
    if (!shader) {
        console.error("Unable to create shader");
        return null;
    }
    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.error("An error occurred compiling the shaders: " + gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
        return null;
    }

    console.log("Shader compiled successfully");
    return shader;
}

function createProgram(vertexShaderSource, fragmentShaderSource) {
    const vertexShader = loadShader(gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = loadShader(gl.FRAGMENT_SHADER, fragmentShaderSource);

    if (!vertexShader || !fragmentShader) {
        console.error("Failed to create shaders");
        return null;
    }

    const program = gl.createProgram();
    if (!program) {
        console.error("Unable to create program");
        return null;
    }

    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        console.error("Unable to initialize the shader program: " + gl.getProgramInfoLog(program));
        gl.deleteProgram(program);
        return null;
    }

    return program;
}

// load image as texture
async function loadTexture(url) {
    const image = new Image();

    let src;

    if (previewZip[url]) {
        src = previewZip[url];
    } else {
        src = url;
    }

    const buf = await fetchWithAuth(src).then(res => res.blob());

    if (!buf) {
        console.error("Failed to fetch image:", url);
        return null;
    }

    image.src = URL.createObjectURL(buf);

    return new Promise((resolve, reject) => {
        image.onload = () => {
            const texture = gl.createTexture();
            gl.bindTexture(gl.TEXTURE_2D, texture);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

            console.log("Texture loaded successfully:", url);

            resolve(texture);
        };
        image.onerror = (err) => {
            console.error("Failed to load texture:", err);
            reject(err);
        };
    });
}

async function loadImageWithPreview(key, path) {
    const texture = await loadTexture(`/api/v1/resource/${path}`);

    if (!texture) {
        console.error("Failed to load preview texture for:", key);
        throw new Error("Failed to load preview texture");
    }

    textureCache[key] = texture;
    //
    // console.log("Preview texture loaded for:", key);
    //
    // (async () => {
    //     // wait for previewLoaded to be true
    //     while (!previewLoaded) {
    //         await new Promise(resolve => setTimeout(resolve, 1000));
    //     }
    //
    //     // Load the main texture
    //     const mainTexture = await loadTexture(path + ".png");
    //     if (!mainTexture) {
    //         console.error("Failed to load main texture for:", key);
    //         throw new Error("Failed to load main texture");
    //     }
    //
    //     textureCache[key] = mainTexture;
    //     console.log("Main texture loaded for:", key);
    // })().catch(err => {
    //     console.error("Error loading main texture for:", key, err);
    // });
}

async function loadVideoTexture(keys, path, cropWidth = 0, cropHeight = 0) {
    // await Promise.all(keys.map(async (key) => {
    //     const texture = await loadTexture(`/api/v1/resource/${key}`);
    //     if (!texture) {
    //         console.error(`Failed to load video texture for key: ${key}`);
    //         throw new Error(`Failed to load video texture for key: ${key}`);
    //     }
    //     textureCache[key] = texture;
    // }));

    const buffer = await fetchWithAuth(`/api/v1/resource/${path}`).then(res => res.arrayBuffer());
    const frames = await extractFramesFromH264(gl, buffer);

    console.log(`NUM FRAMES: ${frames.length}, NUM KEYS: ${keys.length}`);

    if (!frames || frames.length === 0) {
        console.error("Failed to extract frames from video:", path);
        throw new Error("Failed to extract frames from video");
    }

    if (frames.length !== keys.length) {
        console.warn(`Number of frames (${frames.length}) does not match number of keys (${keys.length})`);
    }

    for (let i = 0; i < frames.length; i++) {
        const key = keys[i];
        const frame = frames[i];

        if (!frame) {
            console.error(`Frame ${i} is invalid for key: ${key}`);
            continue;
        }

        textureCache[key] = frame;
        console.log(`Video texture loaded for key: ${key}`);
    }
}

function appendEmotionQueue(emotion, intro = true, filter = ['eye', 'lip']) {
    const eyeAnimData = configData.eye_resource[emotion];
    const lipAnimData = configData.lip_resource[emotion];

    if (filter.includes('eye') && eyeAnimData) {
        if (intro) {
            for (let i = 0; i < eyeAnimData.files.length / 2; i++) {
                const file = eyeAnimData.files[i];
                const key = `${eyeAnimData.path}/${file}`;

                eyeAnimation.push([1000 / 30, key]); // 30 FPS
            }
        } else {
            for (let i = Math.floor(eyeAnimData.files.length / 2); i < eyeAnimData.files.length; i++) {
                const file = eyeAnimData.files[i];
                const key = `${eyeAnimData.path}/${file}`;

                eyeAnimation.push([1000 / 30, key]); // 30 FPS
            }
        }
    }

    if (filter.includes('lip') && lipAnimData) {
        if (intro) {
            for (let i = 0; i < lipAnimData.files.length / 2; i++) {
                const file = lipAnimData.files[i];
                const key = `${lipAnimData.path}/${file}`;

                lipAnimation.push([1000 / 30, key]); // 30 FPS
            }
        } else {
            for (let i = Math.floor(lipAnimData.files.length / 2); i < lipAnimData.files.length; i++) {
                const file = lipAnimData.files[i];
                const key = `${lipAnimData.path}/${file}`;

                lipAnimation.push([1000 / 30, key]); // 30 FPS
            }
        }
    }

    return Math.max(eyeAnimation.reduce((a, b) => a + b[0], 0), lipAnimation.reduce((a, b) => a + b[0], 0));
}

function setEmotion(emotion, intro = true) {
    lipIdleAnim.length = 0;
    eyeIdleAnim.length = 0;

    lipIdleAnim.push([
        10,
        "/data/lip_idle.png" // Default idle texture
    ]);

    eyeIdleAnim.push([
        5000, // 5 second for idle
        "/data/eye_idle.png" // Default idle texture
    ]);

    if (emotion === "idle") {
        const animationData = configData.eye_resource["blink"];
        if (animationData && animationData.files) {
            console.log("Loading eye idle animation frames:", animationData.files);
            for (let i = 0; i < animationData.files.length; i++) {
                const file = animationData.files[i];
                const key = `${animationData.path}/${file}`;
                eyeIdleAnim.push([1000 / 30, key]); // 30 FPS
            }
            console.log(`Eye idle animation frames loaded: ${eyeIdleAnim.length}`,);
        } else {
            console.error("No eye idle animation data found in config");
        }

        return;
    }

    appendEmotionQueue(emotion, intro);

    const eyeAnimData = configData.eye_resource[emotion];
    const lipAnimData = configData.lip_resource[emotion];

    console.log(`Setting emotion: ${emotion}`);

    if (eyeAnimData) {
        eyeIdleAnim.length = 0;

        const animationData = configData.eye_resource[emotion];
        if (animationData && animationData.files) {
            console.log("Loading eye animation frames:", animationData.files);

            if (intro) {
                const mid = Math.floor(animationData.files.length / 2);
                const midFile = animationData.files[mid];
                const midKey = `${animationData.path}/${midFile}`;

                eyeIdleAnim.push([
                    5000, // 5 second for idle
                    midKey // Middle frame as idle,
                ]);
            } else {
                eyeIdleAnim.push([
                    5000, // 5 second for idle
                    "/data/eye_idle.png" // Default idle texture
                ]);
            }
            console.log(`Eye animation frames loaded: ${eyeIdleAnim.length}`);
        } else {
            console.error("No eye animation data found in config for emotion:", emotion);
        }
    }

    if (lipAnimData) {
        lipIdleAnim.length = 0;

        const animationData = configData.lip_resource[emotion];
        if (animationData && animationData.files) {
            console.log("Loading lip animation frames:", animationData.files);

            if (intro) {
                const mid = Math.floor(animationData.files.length / 2);
                const midFile = animationData.files[mid];
                const midKey = `${animationData.path}/${midFile}`;

                lipIdleAnim.push([
                    10,
                    midKey // Middle frame as idle
                ]);
            } else {
                lipIdleAnim.push([
                    10,
                    "/data/lip_idle.png" // Default idle texture
                ]);
            }

            lipAnimationEnabled = true;

            console.log(`Lip animation frames loaded: ${lipIdleAnim.length}`);
        } else {
            console.error("No lip animation data found in config for emotion:", emotion);
        }
    }
}

async function fetchMessage(message, token, endpoint) {
    const response = await fetchWithAuth(endpoint, {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify({message, token})
    });

    if (!response.ok) {
        throw new Error("Network response was not ok");
    }

    const data = await response.json();
    return data;
}

let prevDt = 0;
let eyeFrameDt = 0;
let lipFrameDt = 0;

function render(dt) {
    const deltaTime = dt - prevDt;
    prevDt = dt;

    if (deltaTime === 0) {
        return;
    }

    // console.log("Rendering frame...");
    // clear the canvas
    const canvas = document.getElementById("canvas");
    if (!canvas) return;

    gl = canvas.getContext("webgl");
    if (!gl) {
        console.error("WebGL not supported, falling back to experimental-webgl");
        return;
    }

    const base_offset = [
        configData.full_body_size[0] / 2,
        configData.full_body_size[1],
    ];

    let eyeTexture = textureCache["eye_idle"];
    let lipTexture = textureCache["lip_idle"];

    const eyeFrameTime = dt - eyeFrameDt;

    if (eyeAnimation.length > 0) {
        eyeIdleFrame = 0; // Reset idle frame when an animation is playing

        const eyeAnim = eyeAnimation[0];
        eyeTexture = textureCache[eyeAnim[1]];

        if (eyeFrameTime > eyeAnim[0]) {
            eyeFrameDt += eyeAnim[0]; // Update the frame time
            eyeAnimation.shift(); // Remove the first animation frame
        }
    } else {
        const eyeAnim = eyeIdleAnim[eyeIdleFrame % eyeIdleAnim.length];
        eyeTexture = textureCache[eyeAnim[1]];

        if (eyeFrameTime > eyeAnim[0]) {
            eyeIdleFrame++;
            eyeFrameDt = dt;
        }
    }

    const lipFrameTime = dt - lipFrameDt;

    if (lipAnimationEnabled && lipAnimation.length > 0) {
        lipIdleFrame = 0; // Reset idle frame when an animation is playing

        const lipAnim = lipAnimation[0];
        lipTexture = textureCache[lipAnim[1]];

        // console.log("Current lip anmation frame:", lipAnim[1], "Duration:", lipAnim[0], "Frame time:", lipFrameTime);

        if (lipFrameTime > lipAnim[0]) {
            lipFrameDt += lipAnim[0]; // Update the frame time
            lipAnimation.shift(); // Remove the first animation frame
        }
    } else {
        const lipAnim = lipIdleAnim[lipIdleFrame % lipIdleAnim.length];
        lipTexture = textureCache[lipAnim[1]];
        if (lipFrameTime > lipAnim[0]) {
            lipIdleFrame++;
            lipFrameDt = dt;
        }
    }

    const eyeWidth = configData.coordinates.upper_face.lower_right[0] - configData.coordinates.upper_face.upper_left[0];
    const eyeHeight = configData.coordinates.upper_face.lower_right[1] - configData.coordinates.upper_face.upper_left[1];
    const lipWidth = configData.coordinates.lower_face.lower_right[0] - configData.coordinates.lower_face.upper_left[0];
    const lipHeight = configData.coordinates.lower_face.lower_right[1] - configData.coordinates.lower_face.upper_left[1];

    // Set the viewport to match the canvas size
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.clearColor(0.0, 0.0, 0.0, 0.0); // Set clear color to black
    gl.clear(gl.COLOR_BUFFER_BIT); // Clear the color buffer

    // // 1) 블렌딩 기능 켜기
    // gl.enable(gl.BLEND);
    //
    // // 2) 소스‑알파 vs 대상‑알파 가중치 설정
    // //    가장 흔한 ‘표준’ 알파 블렌드
    // gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    // Use the default program
    const program = programCache["default"];
    if (!program) {
        console.error("No default program found");
        return;
    }

    gl.useProgram(program);

    gl.bindBuffer(gl.ARRAY_BUFFER, bufferCache["default"]);

    const positionLocation = gl.getAttribLocation(program, "aVertexPosition");
    if (positionLocation < 0) {
        console.error("Failed to get attribute location for aVertexPosition");
        return;
    }
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

    gl.uniform2f(gl.getUniformLocation(program, "uScreenSize"), canvas.width, canvas.height);

    // bind mask texture if available
    if (textureCache["mask"]) {
        const maskTexture = textureCache["mask"];
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, maskTexture);
        const uMaskSampler = gl.getUniformLocation(program, "uMaskSampler");
        if (uMaskSampler) {
            gl.uniform1i(uMaskSampler, 1); // Texture unit 1 for mask
        } else {
            console.error("Failed to get uniform location for uMaskSampler");
        }
        gl.activeTexture(gl.TEXTURE0); // Switch back to texture unit 0 for the main texture
    }

    // set mask enabled to 0
    const uMaskEnabled = gl.getUniformLocation(program, "uMaskEnabled");
    if (uMaskEnabled) {
        gl.uniform1i(uMaskEnabled, 0);
    } else {
        console.error("Failed to get uniform location for uMaskEnabled");
    }

    // enable mask
    gl.uniform1i(uMaskEnabled, 1);

    // Bind the texture if available
    {
        const texture = textureCache["body"];
        if (texture) {
            gl.bindTexture(gl.TEXTURE_2D, texture);
            gl.uniform4f(gl.getUniformLocation(program, "uImageCoord"), 0, 0, canvas.width, canvas.height);
            const uSampler = gl.getUniformLocation(program, "uSampler");
            if (uSampler) {
                gl.uniform1i(uSampler, 0); // Texture unit 0
            } else {
                console.error("Failed to get uniform location for uSampler");
            }
            // Draw the rectangle
            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        }
    }

    {
        const texture = textureCache["background_image"];
        if (texture) {
            gl.bindTexture(gl.TEXTURE_2D, texture);
            const right = base_offset[0] + configData.base_coordinates.lower_right[0];
            const bottom = base_offset[1] + configData.base_coordinates.lower_right[1];
            const left = base_offset[0] + configData.base_coordinates.upper_left[0];
            const top = base_offset[1] + configData.base_coordinates.upper_left[1];
            gl.uniform4f(gl.getUniformLocation(program, "uImageCoord"), left, top, right - left, bottom - top);
            const uSampler = gl.getUniformLocation(program, "uSampler");
            if (uSampler) {
                gl.uniform1i(uSampler, 0); // Texture unit 0
            } else {
                console.error("Failed to get uniform location for uSampler");
            }
            // Draw the rectangle
            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        }
    }

    {
        if (lipTexture) {
            gl.bindTexture(gl.TEXTURE_2D, lipTexture);
            const left = base_offset[0] + configData.coordinates.lower_face.upper_left[0];
            const top = base_offset[1] + configData.coordinates.lower_face.upper_left[1];
            const width = lipWidth;
            const height = lipHeight;
            // console.log("Lip idle texture coordinates:", left, top, width, height);
            gl.uniform4f(gl.getUniformLocation(program, "uImageCoord"), left, top, width, height);
            const uSampler = gl.getUniformLocation(program, "uSampler");
            if (uSampler) {
                gl.uniform1i(uSampler, 0); // Texture unit 0
            } else {
                console.error("Failed to get uniform location for uSampler");
            }
            // Draw the rectangle
            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        }
    }

    {
        if (eyeTexture) {
            gl.bindTexture(gl.TEXTURE_2D, eyeTexture);
            const left = base_offset[0] + configData.coordinates.upper_face.upper_left[0];
            const top = base_offset[1] + configData.coordinates.upper_face.upper_left[1];
            const width = eyeWidth;
            const height = eyeHeight;
            // console.log("Eye idle texture coordinates:", left, top, width, height);
            gl.uniform4f(gl.getUniformLocation(program, "uImageCoord"), left, top, width, height);
            const uSampler = gl.getUniformLocation(program, "uSampler");
            if (uSampler) {
                gl.uniform1i(uSampler, 0); // Texture unit 0
            } else {
                console.error("Failed to get uniform location for uSampler");
            }
            // Draw the rectangle
            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        }
    }

    // Unbind the buffer
    // gl.bindBuffer(gl.ARRAY_BUFFER, null);
    // gl.bindTexture(gl.TEXTURE_2D, null);

    // Cleanup
    // gl.useProgram(null);
}

function ChatBotLog({log, onEnded, flush}) {
    const [currentLog, setCurrentLog] = useState("");

    let index = useRef(0);

    // Effect to handle typing effect
    useEffect(() => {
        const typingInterval = setInterval(() => {
            if (flush) {
                clearInterval(typingInterval);
                setCurrentLog(log);
                onEnded();
                return;
            }

            if (index.current < log.length) {
                const c = log[index.current];

                if (c) {
                    setCurrentLog((prev) => prev + c);
                }

                onEnded();
                index.current += 1;
            } else {
                onEnded();
                clearInterval(typingInterval);
            }
        }, 50); // Adjust typing speed here

        return () => clearInterval(typingInterval);
    }, [log, onEnded, flush]);

    return (
        <div className={"msg log-bot"}>
            {currentLog}
        </div>
    )
}

function App() {
    const [isLoading, setIsLoading] = useState(true);
    const [logs, setLogs] = useState([]);
    const [isActive, setIsActive] = useState(false);
    const [inputText, setInputText] = useState("");
    const [userInfo, setUserInfo] = useState(null);
    const [audioData, setAudioData] = useState(null);
    const [animQueue, setAnimQueue] = useState([]);
    const [playAnimation, setPlayAnimation] = useState(false);

    const [isIframe, setIsIframe] = useState(IS_IN_IFRAME);
    const [chatEndpoint, setChatEndpoint] = useState("/api/v1/speak");

    const [mode, setmode] = useState("chat");

    const [roudness, setRoudness] = useState(0);

    const personName = "澁谷 毅";

    useEffect(() => {
        (async () => {
            let savedUserId = localStorage.getItem("userId");

            if (!savedUserId) {
                // Generate a new user ID if not found
                savedUserId = crypto.randomUUID().toString();
                localStorage.setItem("userId", savedUserId);
            }

            console.log("Initializing WebGL...");
            // Initialize the canvas for WebGL
            initCanvas();

            if (!gl) {
                console.error("Failed to initialize WebGL context");
                return;
            }

            // load a texture
            await Promise.all([
                loadImageWithPreview("body", "/full_body_image.jpg"),
                loadImageWithPreview("background_image", "/background_image.jpg"),
                loadImageWithPreview("mask", "/full_body_mask.png"),
                loadImageWithPreview("eye_idle", "/eye_idle.jpg"),
                loadImageWithPreview("lip_idle", "/lip_idle.jpg"),
                (async () => {
                    const configJson = await fetchWithAuth("/api/v1/resource/config.json");

                    if (!configJson.ok) {
                        console.error("Failed to fetch config.json:", configJson.statusText);
                        throw new Error("Failed to fetch config.json");
                    }

                    configData = await configJson.json();
                    console.log("Config data loaded:", configData);
                })(),
                (async () => {
                    const npcJson = await fetchWithAuth("/api/v1/room/npc");

                    if (!npcJson.ok) {
                        console.error("Failed to fetch NPC data:", npcJson.statusText);
                        throw new Error("Failed to fetch NPC data");
                    }

                    npcData = await npcJson.json();
                    console.log("NPC data loaded:", npcData);
                })(),
            ]);

            // Load shaders and create program
            const vertexShaderSource = `
            precision mediump float;
            
            attribute vec4 aVertexPosition;
            
            varying vec2 vTextureCoord;
            varying vec2 vMaskCoord;
            
            uniform vec2 uScreenSize; // screen size
            uniform vec4 uImageCoord; // xywh coordinates for the image
            
            void main(void) {            
                // Set the position of the vertex
                gl_Position = vec4(aVertexPosition.xy, 0.0, 1.0);
                gl_Position.xy = gl_Position.xy * (uImageCoord.zw / uScreenSize);
                gl_Position.xy = gl_Position.xy + vec2(-1.0, 1.0) + vec2(1.0, -1.0) * uImageCoord.zw / uScreenSize.xy;
                gl_Position.xy = gl_Position.xy + vec2(1.0, -1.0) * uImageCoord.xy / uScreenSize.xy * 2.0;
                
                vMaskCoord = (gl_Position.xy * vec2(1, -1) + vec2(1.0, 1.0)) / 2.0; // Normalize to [0, 1]
                
                vTextureCoord = (aVertexPosition.xy * vec2(1, -1) + vec2(1.0, 1.0)) / 2.0; // Normalize to [0, 1]
            }`;
            const fragmentShaderSource = `
            precision mediump float;
            
            varying vec2 vTextureCoord;
            varying vec2 vMaskCoord;
            
            uniform sampler2D uSampler;
            uniform sampler2D uMaskSampler; // Mask texture
            
            uniform int uMaskEnabled; // 0 or 1
            
            void main(void) {
                if (uMaskEnabled == 1) {
                    vec4 maskColor = texture2D(uMaskSampler, vMaskCoord);
                    if (maskColor.r < 0.1) {
                        discard; // Discard pixels based on mask
                    }
                }
                
                // gl_FragColor = vec4(vTextureCoord.xy, 0.0, 1.0); // Simple color based on texture coordinates
                gl_FragColor = texture2D(uSampler, vTextureCoord);
                // if (gl_FragColor.a < 0.1) {
                //     discard; // Discard transparent pixels
                // }
            }`;
            const program = createProgram(vertexShaderSource, fragmentShaderSource);

            if (!program) {
                console.error("Failed to create shader program");
                return;
            }

            programCache["default"] = program;

            // Set up the vertex positions
            const vertices = new Float32Array([
                -1.0, -1.0,
                1.0, -1.0,
                -1.0, 1.0,
                1.0, 1.0,
            ]);
            const vertexBuffer = gl.createBuffer();
            if (!vertexBuffer) {
                console.error("Failed to create vertex buffer");
                return;
            }

            gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer);
            gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

            bufferCache["default"] = vertexBuffer;

            setEmotion("idle");

            // load keysJson
            const [lipKeysJson, eyeKeysJson] = await Promise.all([
                fetchWithAuth("/api/v1/resource/lip.json"),
                fetchWithAuth("/api/v1/resource/eye.json"),
            ]);

            // load lip textures
            if (!lipKeysJson.ok) {
                console.error("Failed to fetch lip.json:", lipKeysJson.statusText);
                return;
            }

            // load eye textures
            if (!eyeKeysJson.ok) {
                console.error("Failed to fetch eye.json:", eyeKeysJson.statusText);
                return;
            }
            const [lipKeys, eyeKeys] = await Promise.all([
                lipKeysJson.json(),
                eyeKeysJson.json(),
            ]);

            // Load video textures for lip and eye animations
            await Promise.all([
                loadVideoTexture(lipKeys, "lip.h264"),
                loadVideoTexture(eyeKeys, "eye.h264"),
            ]);

            console.log("cache textures:", Object.keys(textureCache));

            previewLoaded = true;

            setIsActive(true);
            setIsLoading(false);

            // Start the render loop
            const renderLoop = (dt) => {
                render(dt);
                requestAnimationFrame(renderLoop);
            };
            requestAnimationFrame(renderLoop);

            // Cleanup function to stop the render loop
            return () => {
                cancelAnimationFrame(renderLoop);
            };
        })();
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [logs]);

    async function setAnimData({text, ttsData, visemes}) {
        const vd = multi_viseme2imageClipSequence(visemes.map(v => {
            return {
                "id": v.id,
                "offset": v.ms,
            }
        })).map(v => {
            console.log(v);
            return [
                v.start_time * 10000000, // Convert to milliseconds
                v.end_time * 10000000, // Convert to milliseconds
                v.imageClip_path,
                v.start_frame,
                v.end_frame,
                null,
            ];
        });

        if (!vd || vd.length === 0) {
            console.warn("No viseme data available, using default idle animation");
            lipAnimationEnabled = false;
            lipAnimation.length = 0; // Clear previous animations
            return;
        }

        console.log("Setting animation data:", vd, ttsData);
        lipAnimation.length = 0; // Clear previous lip animations

        const visemeLength = vd[vd.length - 1][1];
        const audioLength = ttsData.extra_info.audio_length;

        console.log("Viseme length:", visemeLength, "Audio length:", audioLength);

        // const syncRatio = audioLength / visemeLength;
        const syncRatio = 1.0;
        console.log("Sync ratio calculated:", syncRatio);

        let lastEndTime = 0;
        let lastFile = "/data/lip_idle.png"; // Default idle texture
        //
        // if (previousEmotion !== "idle") {
        //     appendEmotionQueue(previousEmotion, false);
        // }

        for (let [startTime, endTime, clip, startFrame, endFrame, romaji] of vd) {
            const len = endTime - startTime;
            const clipData = configData.lip_resource[clip];

            if (lastEndTime < startTime) {
                // If there is a gap, add the idle frame
                if (lastFile) {
                    lipAnimation.push([
                        (startTime - lastEndTime) * syncRatio, // Duration for the idle frame
                        lastFile // Use the last file as the idle texture
                    ]);
                }
            }

            lastEndTime = endTime;

            if (endFrame === 0) {
                endFrame = clipData.files.length - 1; // Use the last frame if endFrame is 0
            }

            const files = clipData.files.slice(startFrame, endFrame + 1);

            const timePerFrame = len / files.length;

            for (let i = 0; i < files.length; i++) {
                lipAnimation.push([
                    timePerFrame * syncRatio, // Duration for each frame
                    `${clipData.path}/${files[i]}` // Full path to the texture
                ]);
                lastFile = `${clipData.path}/${files[i]}`; // Update last file
            }
        }

        // console.log("lip Animation: ", JSON.stringify(lipAnimation, null, 2));

        // if (previousEmotion !== "idle") {
        //     appendEmotionQueue(previousEmotion, true);
        // }

        console.log("LipAnimationSequence:", lipAnimation);

        const timeSum = lipAnimation.reduce((sum, anim) => sum + anim[0], 0);
        console.log("Total lip animation time:", timeSum, "ms");

        setAudioData(ttsData.data.audio);
    }

    async function submitMessageInternal(inputText) {
        scrollToBottom();
        setIsActive(false);
        // setLogs((prevLogs) => [...prevLogs, inputText]);

        try {
            const response = await fetchMessage(inputText, userInfo?.access, chatEndpoint);

            console.log(response);

            if (!response.animData) {
                response.animData = response.splitted.map((item, index) => {
                    return new Promise(async (resolve, reject) => {
                        try {
                            await new Promise(resolve => setTimeout(resolve, index * 200)); // Delay for each item

                            const r = await fetch("/api/tts", {
                                method: "POST",
                                headers: {
                                    "Content-Type": "application/json"
                                },
                                body: JSON.stringify({
                                    text: item,
                                }),
                            });

                            if (!r.ok) {
                                throw new Error(`Failed to fetch TTS data: ${r.statusText}`);
                            }

                            resolve(await r.json());
                        } catch (error) {
                            console.error("Error processing item:", item, error);
                            reject(error);
                        }
                    });
                });
            }

            console.log("Response received:", response);

            const queue = response.animData;

            if (window.parentCallback) {
                console.log("Calling parent callback with response message:", response.message);
                window.parentCallback({
                    "type": "aime-chat-response",
                    "message": response.message,
                });
            }

            setAnimQueue(queue);

            (async () => {
                await queue[0];

                if (previousEmotion !== "idle") {
                    lipIdleAnim.length = 0;
                    lipIdleAnim.push([
                        10,
                        "/data/lip_idle.png" // Default idle texture
                    ]);

                    console.log("eye idle anim:", eyeIdleAnim);

                    // eyeIdleAnim.length = 0;
                    // eyeIdleAnim.push([
                    //     5000, // 5 second for idle
                    //     "/data/eye_idle.png" // Default idle texture
                    // ]);

                    console.log("Appending emotion queue for previous emotion:", previousEmotion);

                    const time = appendEmotionQueue(previousEmotion, false, ['lip']);
                    lipAnimationEnabled = true;

                    await new Promise(resolve => setTimeout(resolve, time));
                }

                // setLogs((prevLogs) => [...prevLogs, response.message]);
                setPlayAnimation(true);
                setIsActive(true);
            })();
        } catch (error) {
            console.error("Error fetching message:", error);
            // setLogs((prevLogs) => [...prevLogs, `Error fetching response: ${error.message}`]);
            setIsActive(true);
        }
    }

    function scrollToBottom() {
        // scroll to the bottom of the chat logs whenever logs change
        const logsContainer = document.querySelector(".logs");
        if (logsContainer) {
            logsContainer.scrollTop = logsContainer.scrollHeight;
        }
    }

    async function submitMessage() {
        if (inputText.trim() === "") return;
        if (!isActive) return;

        setInputText("");

        if (inputText === "update") {
            const prevTemplate = npcData.template;

            let nextTemplate;

            if (prevTemplate === "man") {
                nextTemplate = "woman";
            } else {
                nextTemplate = "man";
            }

            const r = await fetchWithAuth("/api/v1/room/update", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    "npc": {
                        "template": nextTemplate,
                        "emotion": "idle"
                    },
                }),
            });

            const data = await r.json();
            console.log("Update response:", data);

            // change token in query string
            const url = new URL(window.location.href);
            url.searchParams.set("token", data.token);
            window.location.href = url.toString();
        }

        if (["idle", "angry", "smile", "laugh"].includes(inputText.toLowerCase())) {
            const emotion = inputText.toLowerCase();

            if (previousEmotion !== emotion) {
                setEmotion(previousEmotion, false);
            }

            setEmotion(emotion);
            previousEmotion = emotion;

            setInputText("");
            return;
        }

        await submitMessageInternal(inputText);
    }

    function onInputButton() {
        unlockAudio();
        switch (mode) {
            case "recording":
                setmode("pause");
                break;
            case "chat":
                setmode("recording");
                break;
            case "pause":
                setmode("chat");
                break;
        }
    }

    if (window.childCallbacks) {
        window.childCallbacks.chat = ({message}) => {
            submitMessageInternal(message);
        }
        window.childCallbacks.updateRoom = async ({data}) => {
            if (data.npc.template === npcData.template) {
                console.log("Template is the same, no need to update");

                const emotion = data.npc.emotion;

                if (previousEmotion !== emotion) {
                    setEmotion(previousEmotion, false);
                }

                setEmotion(emotion);
                previousEmotion = emotion;

                return;
            }

            const r = await fetchWithAuth("/api/v1/room/update", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(data),
            });

            const response = await r.json();
            console.log("Update response:", response);

            // change token in query string
            const url = new URL(window.location.href);
            url.searchParams.set("token", response.token);
            window.location.href = url.toString();
        }
        window.childCallbacks.setEndpoint = (endpoint) => {
            console.log("Setting chat endpoint to:", endpoint);
            setChatEndpoint(endpoint);
        }
    }

    useEffect(() => {
        if (playAnimation) {
            setPlayAnimation(false);
        } else {
            return; // Do nothing if not playing animation
        }

        if (animQueue.length === 0) {
            console.log("Animation queue is empty, nothing to play");

            if (previousEmotion !== "idle") {
                // const eyeAnimation = configData.eye_resource[previousEmotion];
                //
                // if (eyeAnimation) {
                //     eyeIdleAnim.length = 0;
                //
                //     const mid = Math.floor(eyeAnimation.files.length / 2);
                //     const midFile = eyeAnimation.files[mid];
                //     const midKey = `${eyeAnimation.path}/${midFile}`;
                //
                //     eyeIdleAnim.push([
                //         5000, // 5 second for idle
                //         midKey // Middle frame as idle,
                //     ]);
                // }

                const lipAnimation = configData.lip_resource[previousEmotion];

                const mid = Math.floor(lipAnimation.files.length / 2);
                const midFile = lipAnimation.files[mid];
                const midKey = `${lipAnimation.path}/${midFile}`;

                lipIdleAnim.length = 0;
                lipIdleAnim.push([
                    10,
                    midKey // Middle frame as idle
                ]);

                console.log("Appending emotion queue for previous emotion:", previousEmotion);

                appendEmotionQueue(previousEmotion, true, ['lip']);
                lipAnimationEnabled = true;
            }

            return;
        }

        (async () => {
            const animData = await animQueue[0];

            console.log("AnimData:", animData);

            setAnimData(animData);
            setAnimQueue(animQueue.slice(1));
        })();
    }, [animQueue, playAnimation]);

    useEffect(() => {
        if (!audioData) return;

        const blob = pcmHexToWavBlob(audioData);
        setAudioData(null);

        (async () => {
            await speak(blob);

            lipAnimationEnabled = true;
            lipFrameDt = prevDt;

            const hexLen = audioData.length;   // 예: 96 000글자(hex)
            const sampleRate = 44100;  // Hz
            const channels = 1;  // 모노
            const bytesPerSample = 2;  // 16-bit PCM

            const bytes = hexLen / 2;                           // 48 000 byte
            const frames = bytes / (bytesPerSample * channels);  // 24 000 frame
            const seconds = frames / sampleRate;                  // 0.5 초

            clearTimeout(pcmTimeout);

            pcmTimeout = setTimeout(() => {
                lipAnimationEnabled = false;
                setPlayAnimation(true);
            }, (seconds + 0.1) * 1000);
        })();
    }, [audioData]);

    return (
        <div className="wrapper" onContextMenu={(e) => {
            e.preventDefault()
        }}>
            <div className="screen">
                <canvas id="canvas"/>
                <div className={"chat"} style={{display: isIframe ? "none" : "block"}}>
                    <div className={"logs-container"}>
                        <div className={"logs"}>
                            <div className={"logs-top"}/>
                            {
                                logs.map((log, index) => {
                                    if (index % 2 === 0) {
                                        return <div key={index} className={"msg log-user"}>{log}</div>;
                                    } else {
                                        return (
                                            <div key={index} className={"log-bot-container"}>
                                                <span className={"bot-name"}>{personName}</span>
                                                <span className={"bot-name-gap"}/>
                                                <ChatBotLog log={log} flush={index !== logs.length - 1} onEnded={() => {
                                                    scrollToBottom();
                                                }}/>
                                            </div>
                                        );
                                    }
                                })
                            }
                        </div>
                    </div>
                    <div className={"input-area"}>
                        <input id={"input-text"} type={"text"} value={inputText}
                               onChange={(e) => {
                                   setInputText(e.target.value);
                               }} onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                unlockAudio();
                                e.preventDefault();
                                submitMessage();
                            }
                        }}/>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default App;
